package chand.wordvoyage.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource
import wordvoyage.shared.generated.resources.Res
import wordvoyage.shared.generated.resources.ic_volume_up
import wordvoyage.shared.generated.resources.ic_link

// Distinct color per section type
val SectionColorPronunciation = Color(0xFF00897B) // Teal
val SectionColorMeaning       = Color(0xFF6200EA) // Violet
val SectionColorUsage         = Color(0xFF0277BD) // Blue
val SectionColorExamples      = Color(0xFFE65100) // Orange
val SectionColorLearnMore     = Color(0xFF2E7D32) // Green

/**
 * A colorful card with a solid-color header band and white body — used for every detail section.
 */
@Composable
fun FancyDetailCard(
    title: String,
    icon: DrawableResource,
    accentColor: Color,
    onSpeak: (() -> Unit)? = null,
    content: @Composable () -> Unit
) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(14.dp),
        elevation = CardDefaults.cardElevation(defaultElevation = 3.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White)
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            // Colored header
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(
                        color = accentColor,
                        shape = RoundedCornerShape(topStart = 14.dp, topEnd = 14.dp)
                    )
                    .padding(horizontal = 16.dp, vertical = 10.dp)
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Icon(
                            painter = painterResource(icon),
                            contentDescription = null,
                            tint = Color.White,
                            modifier = Modifier.size(20.dp)
                        )
                        Spacer(modifier = Modifier.size(10.dp))
                        Text(
                            text = title,
                            style = MaterialTheme.typography.titleMedium,
                            color = Color.White,
                            letterSpacing = 0.3.sp
                        )
                    }
                    if (onSpeak != null) {
                        IconButton(onClick = onSpeak, modifier = Modifier.size(32.dp)) {
                            Icon(
                                painter = painterResource(Res.drawable.ic_volume_up),
                                contentDescription = "Listen",
                                tint = Color.White,
                                modifier = Modifier.size(20.dp)
                            )
                        }
                    }
                }
            }
            // White body
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(Color.White)
                    .padding(16.dp)
            ) {
                content()
            }
        }
    }
}

/**
 * Fancy word hero banner — shown at the top of both detail screens.
 */
@Composable
fun WordHeroBanner(
    word: String,
    subtitle: String = "",
    accentColor: Color = SectionColorMeaning
) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .background(
                color = accentColor,
                shape = RoundedCornerShape(bottomStart = 28.dp, bottomEnd = 28.dp)
            )
            .padding(horizontal = 24.dp, vertical = 28.dp)
    ) {
        Column {
            Text(
                text = word,
                style = MaterialTheme.typography.displayMedium,
                color = Color.White,
                letterSpacing = 1.sp,
                lineHeight = 44.sp
            )
            if (subtitle.isNotBlank()) {
                Spacer(modifier = Modifier.height(6.dp))
                Text(
                    text = subtitle,
                    style = MaterialTheme.typography.bodyMedium,
                    color = Color.White.copy(alpha = 0.85f)
                )
            }
        }
    }
}

/**
 * Fancy clickable link card.
 */
@Composable
fun FancyLinkCard(url: String, onClick: () -> Unit) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(14.dp),
        elevation = CardDefaults.cardElevation(defaultElevation = 3.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White)
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(
                        color = SectionColorLearnMore,
                        shape = RoundedCornerShape(topStart = 14.dp, topEnd = 14.dp)
                    )
                    .padding(horizontal = 16.dp, vertical = 10.dp)
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(
                        painter = painterResource(Res.drawable.ic_link),
                        contentDescription = null,
                        tint = Color.White,
                        modifier = Modifier.size(20.dp)
                    )
                    Spacer(modifier = Modifier.size(10.dp))
                    Text(
                        text = "Learn More",
                        style = MaterialTheme.typography.titleMedium,
                        color = Color.White,
                        letterSpacing = 0.3.sp
                    )
                }
            }
            Text(
                text = url,
                style = MaterialTheme.typography.bodyMedium,
                color = SectionColorLearnMore,
                textDecoration = TextDecoration.Underline,
                modifier = Modifier
                    .fillMaxWidth()
                    .background(Color.White)
                    .clickable { onClick() }
                    .padding(16.dp)
            )
        }
    }
}

